import {isEmpty} from "lodash";

import {FluxLogger} from "modules/storage_engine/connectors/LogConnector";

import {
    applyAddGlobalRulesFromClipboard,
    applyAddSubjectsFromClipboard,
    applyAddSubjectsPropertiesFromClipboard,
} from "../actions/clipboardActions";
import {performDocumentMutation} from "../documentMutations";
import type {DocumentStoreState} from "../types";

import type {SliceDependencies} from "./types";

type ClipboardSliceState = Pick<
    DocumentStoreState,
    "addSubjectsPropertiesFromClipboard" | "addGlobalRulesFromClipboard" | "addSubjectsFromClipboard" | "pasteSubjects"
>;

export function createClipboardSlice(deps: SliceDependencies): ClipboardSliceState {
    const {primitives} = deps;

    return {
        addSubjectsPropertiesFromClipboard: (targetElementUids, targetRouteUids, properties) => {
            if (!properties || isEmpty(properties)) {
                return;
            }
            performDocumentMutation(
                primitives,
                (draft) =>
                    applyAddSubjectsPropertiesFromClipboard(draft, targetElementUids, targetRouteUids, properties),
                {
                    actionType: "addSubjectsPropertiesFromClipboard",
                    canUndo: true,
                    shouldGenerateActionRecord: true,
                    updatesDocumentTimestamp: true,
                },
            );
        },

        addGlobalRulesFromClipboard: (targetObjectUids, nodeLayoutRules) => {
            if (!targetObjectUids || targetObjectUids.length === 0) {
                return;
            }
            if (!nodeLayoutRules || isEmpty(nodeLayoutRules)) {
                return;
            }
            performDocumentMutation(
                primitives,
                (draft) => applyAddGlobalRulesFromClipboard(draft, targetObjectUids, nodeLayoutRules),
                {
                    actionType: "addGlobalRulesFromClipboard",
                    canUndo: true,
                    shouldGenerateActionRecord: true,
                    updatesDocumentTimestamp: true,
                    additionalPayload: {
                        hasInheritedRules: true,
                        hasInheritedCalculations: true,
                        requiresDescendantRebake: true,
                        requiresNetDescendantRebake: true,
                    },
                },
            );
        },

        addSubjectsFromClipboard: (elements, routeVertices) => {
            if (!elements || elements.length === 0) {
                return;
            }
            try {
                performDocumentMutation(
                    primitives,
                    (draft) => applyAddSubjectsFromClipboard(draft, elements, routeVertices),
                    {
                        actionType: "addSubjectsFromClipboard",
                        canUndo: true,
                        shouldGenerateActionRecord: true,
                        updatesDocumentTimestamp: true,
                    },
                );
            } catch (error) {
                FluxLogger.captureError(error);
            }
        },

        pasteSubjects: (elements, routeVertices, uidsToSelect) => {
            if (!elements || elements.length === 0) {
                return;
            }
            try {
                performDocumentMutation(
                    primitives,
                    (draft) => {
                        applyAddSubjectsFromClipboard(draft, elements, routeVertices);
                        draft.selectedObjectUids = uidsToSelect;
                    },
                    {
                        actionType: "pasteSubjects",
                        canUndo: true,
                        shouldGenerateActionRecord: true,
                        updatesDocumentTimestamp: true,
                    },
                );
            } catch (error) {
                FluxLogger.captureError(error);
            }
        },
    };
}
